import { type ComputedRef, computed } from 'vue'
import type { RouteLocationRaw } from 'vue-router'
import { ButtonVariant } from './types'

interface Params {
  to: ComputedRef<RouteLocationRaw | undefined>
  disabled?: ComputedRef<boolean>
  variant?: ComputedRef<keyof typeof ButtonVariant>
}

export default function ({ to, disabled, variant }: Params) {
  const tag = computed(() => {
    if (to?.value)
      return 'nuxt-link'
    return 'button'
  })

  const bound = computed(() => {
    switch (tag.value) {
      case 'nuxt-link':
        return { to: to.value }
      case 'button':
      default:
        return { disabled: disabled?.value }
    }
  })

  const variantClass = computed(() => {
    const cls = [
      'inline-flex',
      'flex-grow-0',
      'cursor-pointer',
      'items-center',
      'justify-center',
      'gap-2',
      'rounded-md',
      'border-0',
      'font-medium',
    ]
    if (disabled?.value)
      cls.push('!cursor-not-allowed opacity-[0.43]')

    switch (variant?.value) {
      case ButtonVariant['default-red']:
        cls.push(
          'bg-white text-red-500 hover:bg-gray-50 hover:no-underline active:bg-gray-100 ring-inset ring-1 ring-gray-200',
        )
        break
      case ButtonVariant.red:
        cls.push(
          'bg-red antialiased text-white hover:bg-red-500 hover:no-underline hover:text-white active:bg-red-600',
        )
        break
      case ButtonVariant.blue:
        cls.push(
          'bg-blue antialiased text-white hover:bg-blue-700 hover:no-underline hover:text-white active:bg-blue-800',
        )
        break
      case ButtonVariant['light-blue']:
        cls.push(
          'bg-blue-100 antialiased text-blue-600 hover:bg-blue-200 hover:no-underline hover:text-blue-700 active:bg-blue-200 ring-inset ring-1 ring-blue-400/10',
        )
        break
      case ButtonVariant.green:
        cls.push(
          'bg-green antialiased text-white hover:bg-green-700 hover:text-white hover:no-underline active:bg-green-800',
        )
        break
      case ButtonVariant.plain:
        cls.push(
          'bg-transparent text-gray-800 hover:bg-gray-600/5 hover:no-underline active:bg-gray-600/10',
        )
        break
      case ButtonVariant.link:
        cls.push('text-link hover:text-link-hover underline')
        break
      case ButtonVariant['link-red']:
        cls.push('text-red-500 hover:text-red-600 underline')
        break
      case ButtonVariant.default:
      default:
        cls.push(
          'bg-white text-gray-700 hover:bg-gray-50 hover:no-underline active:bg-gray-100 ring-1 ring-gray-200',
        )
        break
    }
    return cls
  })

  return {
    tag,
    bound,
    variantClass,
  }
}
